import React, { useState } from 'react';
import { useI18n, useSelector, useStaffCan, useLogger } from '~Hooks';

import HandyPageWrapper from '~Scenes/Handy/Components/HandyPageWrapper';
import HandySettingsBlock, {
  SettingBlockItemType,
} from '~Scenes/Handy/Components/HandySettingsBlock';
import { IconType } from '~Scenes/Handy/Components/RoundedIcon';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import { DeveloperKeypass } from '~Scenes/Handy/Scenes/Settings/General/DeveloperKeypass';
import { StorageData, useStorage } from '~/Components/Storage';
import { SERVICE_TYPE } from '~../__queries__/globalTypes';
import styled from '@emotion/styled';
import Logout from '~Scenes/Settings-v2/Components/Logout';

const LogoutWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 64px;

  & > * {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
`;

const General = () => {
  const page = usePage();
  const staffCan = useStaffCan();
  const flags = useSelector((state) => state.companyFlags);

  const { i18n } = useI18n();
  const [data, commitToStorage] = useStorage();
  const logger = useLogger('handy-settings');

  const updateStorage = (data: Partial<StorageData>) => {
    logger.info('Updating Settings from Handy', { fields: data });
    commitToStorage(data);
  };

  const [showDeveloperKeypass, setShowDeveloperKeypass] = useState(false);

  const handleDeveloperPassSubmit = (value: string) => {
    if (value === '8888') {
      page.navigateTo('developer');
      setShowDeveloperKeypass(false);
    }
  };

  const navigationBlocks: SettingBlockItemType[] = [
    {
      id: 'language-settings',
      title: i18n('Handy.Language'),
      bigTitle: true,
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('languageSettings');
        },
      },
    },
    {
      id: 'default-takeaway',
      title: i18n('Settings.DefaultTakeAway'),
      description: i18n('Settings.DefaultTakeAway.Text'),
      action: {
        onClick: () =>
          updateStorage({
            serviceType:
              data.serviceType === SERVICE_TYPE.EAT_HERE
                ? SERVICE_TYPE.TAKE_AWAY
                : SERVICE_TYPE.EAT_HERE,
          }),
        isToggle: true,
        selected: data.serviceType === SERVICE_TYPE.TAKE_AWAY,
      },
    },
    {
      id: 'hide-service-type',
      title: i18n('Settings.HideServiceType'),
      action: {
        onClick: () =>
          updateStorage({
            hideServiceType: !data.hideServiceType,
          }),
        isToggle: true,
        selected: data.hideServiceType,
      },
    },
    {
      id: 'developers-settings',
      title: i18n('Handy.Settings.Developer'),
      bigTitle: true,
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          setShowDeveloperKeypass(true);
        },
      },
    },
  ];

  if (flags.hasPoints) {
    navigationBlocks.push({
      id: 'qr-on-cheque',
      title: i18n('Settings.PrintChequeQR'),
      description: i18n('Settings.PrintChequeQR.Text'),
      action: {
        onClick: () =>
          updateStorage({
            printQROnCheque: !data.printQROnCheque,
          }),
        isToggle: true,
        selected: data.printQROnCheque,
      },
    });
  }

  // Show Terminal navigation when adyen terminals are configured
  if (data.adyenTerminals?.length > 0) {
    navigationBlocks.push({
      id: 'terminal-mode',
      title: 'Terminal',
      bigTitle: true,
      action: {
        icon: {
          type: IconType.FORWARD_ARROW,
        },
        onClick: () => {
          page.navigateTo('managed-payments');
        },
      },
    });
  }

  // Always append this last
  navigationBlocks.push({
    id: 'developers-settings',
    title: i18n('Handy.Settings.Developer'),
    bigTitle: true,
    action: {
      icon: {
        type: IconType.FORWARD_ARROW,
      },
      onClick: () => {
        setShowDeveloperKeypass(true);
      },
    },
  });

  return (
    <HandyPageWrapper pageTitle={i18n('Handy.Settings.GeneralTitle')}>
      <DeveloperKeypass
        open={showDeveloperKeypass}
        onClose={() => setShowDeveloperKeypass(false)}
        onSubmit={handleDeveloperPassSubmit}
      />
      <HandySettingsBlock items={navigationBlocks} />
      {staffCan.LOG_OUT && (
        <LogoutWrapper>
          <Logout />
        </LogoutWrapper>
      )}
    </HandyPageWrapper>
  );
};

export default General;
